/**
 * Exports a scenario's costed regime breakdown to an Excel workbook.
 * Produces sheets for summary, trade, schedule, facility, criticality, and data quality breakdowns.
 *
 * @param {object} params
 * @param {object} params.data - The scenario object with costResult
 * @param {string} params.userName - The exporting user's display name
 */
import { getCurrencySymbol, formatMoney, round2 } from "../format-utils"
import { saveWorkbookForExport } from "./save-workbook-for-export"
import { buildAssetExportRows, buildScheduleExportRows, buildTradeExportRows } from "./cost-breakdown-view-utils"
import { buildDataQualityDetailExportRows } from "./data-quality-detail-utils"

export async function exportScenarioToExcel({ data, userName }) {
    const xlsx = await import("xlsx")
    const { utils } = xlsx
    const { costResult } = data
    if (!costResult) return

    const currencySymbol = getCurrencySymbol(costResult.currency)
    const now = new Date()
    const dateStr = formatIsoDate(now)
    const dateTimeStr = formatDateTime(now)

    const workbook = utils.book_new()

    addSheet(workbook, utils, "Summary", buildSummaryRows({ data, costResult, currencySymbol, dateTimeStr, userName }))
    addSheet(workbook, utils, "By Trade", buildTradeRows({ costResult, currencySymbol }))
    addSheet(workbook, utils, "By Schedule", buildScheduleRows({ costResult, currencySymbol }))
    addSheet(workbook, utils, "By Asset", buildAssetRows({ costResult, currencySymbol }))
    addSheet(workbook, utils, "By Facility", buildFacilityRows({ costResult, currencySymbol }))
    addSheet(workbook, utils, "By Criticality", buildCriticalityRows({ costResult, currencySymbol }))

    if (costResult.assetTypeBreakdown?.length) {
        addSheet(workbook, utils, "By Asset Type", buildAssetTypeRows({ costResult, currencySymbol }))
    }

    const dataQualityRows = buildDataQualityRows({ costResult, currencySymbol })
    if (dataQualityRows.length) {
        addSheet(workbook, utils, "Data Quality", dataQualityRows)
    }

    // FIQ-1658: per-task rows behind each data-quality warning (absent on results
    // calculated before dataQualityDetails existed).
    const dataQualityDetailRows = buildDataQualityDetailExportRows({ costResult, currencySymbol })
    if (dataQualityDetailRows.length) {
        addSheet(workbook, utils, "Data Quality Detail", dataQualityDetailRows)
    }

    const regimeName = sanitiseFileName(data.regimeName || "Regime")
    await saveWorkbookForExport({ workbook, fileName: `Optimiser Costing -- ${regimeName} -- ${dateStr}.xlsx`, xlsx })
}

function addSheet(workbook, utils, sheetName, rows) {
    if (!rows.length) return
    const worksheet = utils.json_to_sheet(rows)
    const keys = Object.keys(rows[0])
    worksheet["!cols"] = keys.map((key) => ({
        wch: rows.reduce((w, r) => Math.max(w, `${r[key] ?? ""}`.length), key.length + 2),
    }))
    utils.book_append_sheet(workbook, worksheet, sheetName)
}

function buildSummaryRows({ data, costResult, currencySymbol, dateTimeStr, userName }) {
    const rows = []
    rows.push({ Field: "Regime Name", Value: data.regimeName || "" })
    rows.push({ Field: "Rate Card", Value: data.rateCardName || "" })
    rows.push({ Field: "Skill Map", Value: data.rateCardSkillMapName || "" })
    rows.push({ Field: "Currency", Value: costResult.currency || "GBP" })
    rows.push({ Field: "Export Date", Value: dateTimeStr })
    rows.push({ Field: "Exported By", Value: userName || "" })
    rows.push({ Field: "", Value: "" })

    rows.push({ Field: "Trade Costs", Value: `${currencySymbol}${formatMoney(costResult.maintenanceSubtotal)}` })
    rows.push({ Field: "", Value: "" })
    rows.push({ Field: "Grand Total", Value: `${currencySymbol}${formatMoney(costResult.grandTotal)}` })
    rows.push({ Field: "", Value: "" })
    rows.push({ Field: "Tasks Costed", Value: `${costResult.costedTaskCount || 0} of ${costResult.taskCount || 0}` })

    if (costResult.unmappedSkills?.count) {
        rows.push({
            Field: "Unmapped Skills",
            Value: `${costResult.unmappedSkills.count} (${costResult.unmappedSkills.hours} hours) - ${(costResult.unmappedSkills.skillNames || []).join(", ")}`,
        })
    }
    if (costResult.unpricedTrades?.count) {
        rows.push({
            Field: "Unpriced Trades",
            Value: `${costResult.unpricedTrades.count} (${costResult.unpricedTrades.hours} hours) - ${(costResult.unpricedTrades.tradeNames || []).join(", ")}`,
        })
    }

    if (buildDataQualityRows({ costResult, currencySymbol }).length) {
        rows.push({ Field: "", Value: "" })
        rows.push({ Field: "Note", Value: "See 'Data Quality' sheet for details on excluded and flagged tasks." })
    }

    return rows
}

function buildTradeRows({ costResult, currencySymbol }) {
    return buildTradeExportRows({ costResult, currencySymbol })
}

export function buildScheduleRows({ costResult, currencySymbol }) {
    return buildScheduleExportRows({ costResult, currencySymbol })
}

function buildAssetRows({ costResult, currencySymbol }) {
    return buildAssetExportRows({ costResult, currencySymbol })
}

function buildFacilityRows({ costResult, currencySymbol }) {
    const facilities = [...(costResult.facilityBreakdown || [])].sort(byTotalCostDesc)
    if (!facilities.length) return []

    const rows = facilities.map(toFacilityRow)

    const totalHours = facilities.reduce(sumTotalHours, 0)
    const totalCost = facilities.reduce(sumTotalCost, 0)

    rows.push({
        Facility: "Total",
        "Total Hours": round2(totalHours),
        "Total Cost": `${currencySymbol}${formatMoney(totalCost)}`,
        "%": "100%",
    })

    return rows

    function toFacilityRow(f) {
        return {
            Facility: f.facilityName,
            "Total Hours": round2(f.totalHours),
            "Total Cost": `${currencySymbol}${formatMoney(f.totalCost)}`,
            "%": `${round2(f.percentage)}%`,
        }
    }
}

function buildCriticalityRows({ costResult, currencySymbol }) {
    const items = [...(costResult.criticalityBreakdown || [])].sort(byTotalCostDesc)
    if (!items.length) return []

    const rows = items.map(toCriticalityRow)

    const totalTasks = items.reduce(sumTaskCount, 0)
    const totalCost = items.reduce(sumTotalCost, 0)

    rows.push({
        Criticality: "Total",
        "Task Count": totalTasks,
        "Total Cost": `${currencySymbol}${formatMoney(totalCost)}`,
        "%": "100%",
    })

    return rows

    function toCriticalityRow(c) {
        return {
            Criticality: c.classification,
            "Task Count": c.taskCount,
            "Total Cost": `${currencySymbol}${formatMoney(c.totalCost)}`,
            "%": `${round2(c.percentage)}%`,
        }
    }
}

function buildDataQualityRows({ costResult, currencySymbol }) {
    const rows = []
    const { transparency } = costResult

    if (costResult.phantomCostTasks?.count > 0) {
        rows.push({
            Category: "Phantom Cost",
            Count: costResult.phantomCostTasks.count,
            Hours: "",
            "Cost Impact": `${currencySymbol}${formatMoney(costResult.phantomCostTasks.totalCost)}`,
            Details: "0-min tasks billed via minimum billing periods",
        })
    }
    if (costResult.unassignedSkills?.count > 0) {
        rows.push({
            Category: "Unassigned Skill",
            Count: costResult.unassignedSkills.count,
            Hours: costResult.unassignedSkills.hours,
            "Cost Impact": "",
            Details: "Tasks with no skill assigned",
        })
    }
    if (costResult.unmappedSkills?.count > 0) {
        rows.push({
            Category: "Unmapped Skill",
            Count: costResult.unmappedSkills.count,
            Hours: costResult.unmappedSkills.hours,
            "Cost Impact": "",
            Details: (costResult.unmappedSkills.skillNames || []).join(", "),
        })
    }
    if (costResult.unpricedTrades?.count > 0) {
        rows.push({
            Category: "Unpriced Trade",
            Count: costResult.unpricedTrades.count,
            Hours: costResult.unpricedTrades.hours,
            "Cost Impact": "",
            Details: (costResult.unpricedTrades.tradeNames || []).join(", "),
        })
    }
    if (transparency?.skippedNoFrequencyCount > 0) {
        rows.push({
            Category: "No Frequency",
            Count: transparency.skippedNoFrequencyCount,
            Hours: "",
            "Cost Impact": "",
            Details: "Tasks excluded - no valid frequency",
        })
    }
    if (transparency?.zeroDurationCount > 0) {
        rows.push({
            Category: "Zero Duration",
            Count: transparency.zeroDurationCount,
            Hours: "",
            "Cost Impact": "",
            Details: "Tasks with zero duration",
        })
    }
    if (transparency?.defaultRateFallback?.count > 0) {
        rows.push({
            Category: "Default Rate Fallback",
            Count: transparency.defaultRateFallback.count,
            Hours: transparency.defaultRateFallback.hours,
            "Cost Impact": "",
            Details: (transparency.defaultRateFallback.tradeNames || []).join(", "),
        })
    }
    if (transparency?.minimumBillingInflation?.affectedCount > 0) {
        const inf = transparency.minimumBillingInflation
        rows.push({
            Category: "Min Billing Inflation",
            Count: inf.affectedCount,
            Hours: `${inf.originalHours} → ${inf.billedHours}`,
            "Cost Impact": "",
            Details: `Billed hours inflated from ${inf.originalHours} to ${inf.billedHours}`,
        })
    }

    return rows
}

function buildAssetTypeRows({ costResult, currencySymbol }) {
    const sorted = [...(costResult.assetTypeBreakdown || [])].sort(byTotalCostDesc)
    if (!sorted.length) return []

    const rows = sorted.map(toAssetTypeRow)

    const totalCost = sorted.reduce(sumTotalCost, 0)
    const totalHours = sorted.reduce(sumTotalHours, 0)
    const totalTasks = sorted.reduce(sumTaskCount, 0)

    rows.push({
        "Asset Type": "Total",
        "Total Cost": `${currencySymbol}${formatMoney(totalCost)}`,
        "Total Hours": round2(totalHours),
        Tasks: totalTasks,
        "%": "100%",
    })

    return rows

    function toAssetTypeRow(a) {
        return {
            "Asset Type": a.assetType,
            "Total Cost": `${currencySymbol}${formatMoney(a.totalCost)}`,
            "Total Hours": round2(a.totalHours),
            Tasks: a.taskCount || 0,
            "%": `${round2(a.percentage)}%`,
        }
    }
}

function formatIsoDate(date) {
    return date.toISOString().split("T")[0]
}

function formatDateTime(date) {
    return `${formatIsoDate(date)} ${date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`
}

function sanitiseFileName(name) {
    return name.replace(/[<>:"/\\|?*]/g, "_")
}

function byTotalCostDesc(a, b) {
    return (b.totalCost || 0) - (a.totalCost || 0)
}

function sumTotalCost(sum, item) {
    return sum + (item.totalCost || 0)
}

function sumTotalHours(sum, item) {
    return sum + (item.totalHours || 0)
}

function sumTaskCount(sum, item) {
    return sum + (item.taskCount || 0)
}
